/**
 * Soft-neon mini visualisations used across the dashboard.
 * Pure SVG, no deps. Animations are CSS-driven via /src/index.css.
 */

export function SparkBars({ values, color = "#D9F99D", height = 36, gap = 3 }) {
  const max = Math.max(...values, 1);
  const w = 120;
  const bw = (w - gap * (values.length - 1)) / values.length;
  return (
    <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} aria-hidden>
      <defs>
        <linearGradient id={`g-${color.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95" />
          <stop offset="100%" stopColor={color} stopOpacity="0.35" />
        </linearGradient>
      </defs>
      {values.map((v, i) => {
        const bh = (v / max) * (height - 4);
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={height - bh}
            width={bw}
            height={bh}
            rx={2}
            fill={`url(#g-${color.replace("#", "")})`}
            className="zx-bar"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        );
      })}
    </svg>
  );
}

export function SparkLine({
  values,
  color = "#D9F99D",
  fill = true,
  width = 260,
  height = 80,
}) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 8) - 4;
    return [x, y];
  });
  const d = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const area = `${d} L${width},${height} L0,${height} Z`;
  const len = Math.ceil(pts.reduce((a, p, i) => (i === 0 ? 0 : a + Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1])), 0));

  const id = color.replace("#", "");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <defs>
        <linearGradient id={`line-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id={`glow-${id}`}>
          <feGaussianBlur stdDeviation="2.2" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {fill && <path d={area} fill={`url(#line-${id})`} />}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow-${id})`}
        className="zx-line-draw"
        style={{ "--len": len }}
      />
      {/* end dot */}
      {pts.length > 0 && (
        <circle
          cx={pts[pts.length - 1][0]}
          cy={pts[pts.length - 1][1]}
          r="3"
          fill={color}
          opacity="0.95"
        />
      )}
    </svg>
  );
}

export function DonutRing({ value = 75, color = "#D9F99D", size = 64, track = "rgba(255,255,255,0.08)" }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={r} stroke={track} strokeWidth="5" fill="none" />
      <circle
        cx={size/2} cy={size/2} r={r}
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ filter: `drop-shadow(0 0 6px ${color}aa)`, transition: "stroke-dashoffset 1.2s cubic-bezier(.2,.8,.2,1)" }}
      />
    </svg>
  );
}
